import { useState, useEffect } from 'react'

/**
 * Detects that a new service worker has taken over, so the app can tell the user and
 * reload onto the new build.
 *
 * The build uses registerType: "autoUpdate", so the incoming worker calls
 * skipWaiting() + clientsClaim() and activates on its own — it never parks in
 * `waiting`. Detection therefore has to cope with the update having *already*
 * happened by the time React mounts:
 *
 *  - The previous version attached its 'updatefound' listener only after
 *    navigator.serviceWorker.ready resolved. registerSW.js registers on window load,
 *    so the update was frequently found and installed BEFORE that listener existed,
 *    and the notice never appeared. Prerendered pages (a ~380KB document) made React
 *    mount even later, so the race was lost almost every time.
 *  - We now inspect the registration for an in-flight worker at mount, and listen for
 *    'controllerchange', which is the one signal guaranteed to fire when a new worker
 *    claims the page.
 */
export const usePWAUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [registration, setRegistration] = useState(null)

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined

    let cancelled = false
    // Was this page already under a service worker's control when it loaded?
    // On a FIRST-EVER visit there is no controller, and clientsClaim() then fires
    // 'controllerchange' as the very first worker takes over. That is initial setup,
    // not a new version — treating it as an update would show "updating…" and reload
    // every new visitor, potentially in a loop.
    const hadControllerAtMount = Boolean(navigator.serviceWorker.controller)
    const markAvailable = () => {
      if (!cancelled && hadControllerAtMount) setUpdateAvailable(true)
    }

    // A worker that reaches 'installed' while another controls the page is an update.
    const watchWorker = (worker) => {
      if (!worker) return
      if (worker.state === 'installed' && navigator.serviceWorker.controller) {
        markAvailable()
        return
      }
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) markAvailable()
      })
    }

    // Fires when the new worker takes control. Under clientsClaim this is the
    // reliable signal, and it works even if we missed 'updatefound' entirely.
    const onControllerChange = () => markAvailable()
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)

    // getRegistration resolves as soon as a registration exists — unlike `ready`,
    // it does not wait for an ACTIVE worker, so the listener is attached earlier.
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || cancelled) return
      setRegistration(reg)

      // An update may already be in flight before this effect ran.
      watchWorker(reg.installing)
      watchWorker(reg.waiting)
      if (reg.waiting && navigator.serviceWorker.controller) markAvailable()

      reg.addEventListener('updatefound', () => watchWorker(reg.installing))
    }).catch(() => { /* no registration — nothing to track */ })

    return () => {
      cancelled = true
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    }
  }, [])

  /**
   * Hand over to the new worker. Only meaningful when one is parked in `waiting`
   * (registerType: "prompt"); under autoUpdate it has already activated.
   * Deliberately does NOT reload — the caller controls that, so the notice stays on
   * screen long enough to be read instead of the page vanishing instantly.
   */
  const updateApp = () => {
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  }

  return { updateAvailable, updateApp }
}
